import { Company, PartialPermission, User } from "@/domain";
import store from "../domain/store";

const lang = store.state.lang.data;

export interface MenuSection {
  key: string;
  subheader?: string;
  items: MenuItem[];
}

export interface MenuItem {
  icon: string;
  text: string;
  to: string;
  hint?: string | null;
}

export function getSidebarSections(
  company: Company | null,
  user: User | null,
  userPermission: PartialPermission
): MenuSection[] {
  if (!user) {
    return [
      {
        key: "no_user",
        items: [
          {
            icon: "mdi-login",
            text: lang.login,
            to: "/authlogin"
          },
          {
            icon: "mdi-account-plus",
            text: lang.signUp,
            to: "/authregister"
          },
          {
            icon: "mdi-face-agent",
            text: lang.contact,
            to: "/contact"
          }
        ]
      }
    ];
  }
  const fullMenu: MenuSection[] = [];

  const toolsMenu = createToolsSection(company, user, userPermission);
  if (toolsMenu.length > 0) {
    fullMenu.push({
      key: "actions",
      subheader: lang.tools,
      items: toolsMenu
    });
  }

  if (company && userPermission.paymentLink) {
    fullMenu.push({
      key: "paymentLink",
      subheader: lang.paymentLink,
      items: [
        {
          icon: "mdi-cash-multiple",
          text: lang.paymentRequest,
          to: "/paymentList",
          hint: lang.paymentRequestHint
        },
        {
          icon: "mdi-cash-refund",
          text: lang.newRequest,
          to: "/paymentEdit",
          hint: lang.newRequestHint
        }
      ]
    });
  }

  const devLinks = createDeveloperMenu(company, userPermission);
  if (devLinks.length > 0) {
    fullMenu.push({
      key: "development",
      subheader: lang.development,
      items: devLinks
    });
  }

  fullMenu.push({
    key: "settings",
    items: [
      {
        icon: "mdi-file-document-edit-outline",
        text: lang.lastChanges,
        to: "/changelog",
        hint: null
      },
      {
        icon: "mdi-face-agent",
        text: lang.contact,
        to: "/contact",
        hint: null
      }
    ]
  });

  return [...fullMenu];
}

function createToolsSection(company: Company | null, user: User, userPermission: PartialPermission): MenuItem[] {
  const toolsMenu: MenuItem[] = [];
  addToolsForCompany(company, userPermission, toolsMenu);
  addToolsForAdmin(userPermission, toolsMenu);
  addToolsForVending(userPermission, toolsMenu);
  addToolsForTopay(userPermission, toolsMenu);
  addToolsForSpecificClients(company, userPermission, toolsMenu);
  addToolsForGlobalAdmin(userPermission, user, toolsMenu);
  return toolsMenu;
}

function addToolsForGlobalAdmin(userPermission: PartialPermission, user: User, toolsMenu: MenuItem[]) {
  if (userPermission.isReinforced && (user.isToken || userPermission.isGlobalAdmin)) {
    toolsMenu.push({
      icon: "admin_panel_settings",
      text: "Administración",
      to: "/management",
      hint: "Administración global del sitio"
    });
  }
}

function addToolsForSpecificClients(company: Company | null, userPermission: PartialPermission, toolsMenu: MenuItem[]) {
  if (company?.id === 60617 && userPermission.euroshellCertificate) {
    toolsMenu.push({
      icon: "mdi-file-certificate",
      text: lang.euroshellCertificates,
      to: "/euroshellcertificate",
      hint: lang.euroshellCertificateHint
    });
  }
}

function addToolsForVending(userPermission: PartialPermission, toolsMenu: MenuItem[]) {
  if (userPermission.vendingAdmin) {
    toolsMenu.push({
      icon: "emoji_food_beverage",
      text: lang.vending,
      to: "/vendingConfig",
      hint: lang.vendingHint
    });
  }
}

function addToolsForTopay(userPermission: PartialPermission, toolsMenu: MenuItem[]) {
  if (userPermission.topayAdmin) {
    toolsMenu.push({
      icon: "mdi-cash-sync",
      text: lang.topay,
      to: "/topayMessages",
      hint: lang.topayHint
    });
  }
}

function addToolsForAdmin(userPermission: PartialPermission, toolsMenu: MenuItem[]) {
  if (userPermission.isReinforced && userPermission.isAdminOfAny) {
    toolsMenu.push({
      icon: "recent_actors",
      text: lang.userManagement,
      to: "/usermanager",
      hint: lang.userManagementHint
    });
    toolsMenu.push({
      icon: "mdi-home-edit-outline",
      text: lang.officeconfig,
      to: "/branchConfig",
      hint: lang.officeconfigHint
    });
  }
}

function addToolsForCompany(company: Company | null, userPermission: PartialPermission, toolsMenu: MenuItem[]) {
  if (company) {
    if (userPermission.allowConnectionGraph) {
      toolsMenu.push({
        icon: "business",
        text: lang.storeMenu,
        to: "/companyInfo",
        hint: lang.storeMenuHint
      });
    }
    if (userPermission.txnView) {
      toolsMenu.push({
        icon: "mdi-ballot",
        text: lang.transactions,
        to: "/transactionquery",
        hint: lang.transactionsHint
      });
    }
    if (userPermission.allowConnectionGraph) {
      toolsMenu.push({
        icon: "network_check",
        text: lang.connectionStatus,
        to: "/connectionstatus",
        hint: lang.connectionStatusHint
      });
    }
  } else {
    toolsMenu.push({
      icon: "business",
      text: lang.myStores,
      to: "/companyCatalog",
      hint: lang.myStoresHint
    });
    if (userPermission.txnView) {
      toolsMenu.push({
        icon: "mdi-ballot",
        text: lang.transactions,
        to: "/transactionquery",
        hint: lang.transactionsHint
      });
    }
  }
}

function createDeveloperMenu(company: Company | null, userPermission: PartialPermission): MenuItem[] {
  if (!userPermission.isReinforced) return [];
  const devLinks: MenuItem[] = [];
  if (userPermission.isAdminOfAny || userPermission.clientAPI) {
    devLinks.push({
      icon: "mdi-code-braces",
      text: lang.clientAPI,
      to: "/clientAPI",
      hint: lang.clientAPIHint
    });
  }

  if (company && userPermission.paymentLink && userPermission.clientAPI) {
    devLinks.push({
      icon: "mdi-code-braces",
      text: lang.paymentLinkAPI,
      to: "/paymentLinkAPI",
      hint: lang.paymentLinkAPIHint
    });
  }
  return devLinks;
}
